import React, { useState, useEffect } from 'react';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import { 
  Container, Box, TextField, Button, Typography, Avatar, 
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, 
  IconButton, CircularProgress, Grid, Stack, styled, useTheme, Fade 
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { decryptData } from '../utils/cryptoUtils.ts';
import { Particles } from '../components/Particles.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';

const DEFAULT_AVATAR = "https://api.dicebear.com/7.x/identicon/svg?seed=default";

const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode, theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
  },
  '& .MuiDialogTitle-root': { fontWeight: 'bold', textAlign: 'center', color: darkMode ? 'white' : '#1a237e' },
}));

const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 24px',
  fontWeight: 'bold',
  color: '#ffffff',
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#25379c',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.25)' : '#1a237e',
    transform: 'translateY(-2px)',
    boxShadow: darkMode ? '0 0 15px rgba(255, 64, 129, 0.4)' : '0 0 15px rgba(33, 203, 243, 0.4)',
  },
  '&:disabled': { backgroundColor: 'rgba(0,0,0,0.1)', color: 'rgba(0,0,0,0.3)' }
}));

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiOutlinedInput-root': {
    color: darkMode ? 'white' : '#1a237e',
    borderRadius: '12px',
    '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)' },
    '&:hover fieldset': { borderColor: darkMode ? 'white' : '#25379c' },
  },
  '& .MuiInputLabel-root': { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' },
}));

const Account: React.FC = () => {
  const { darkMode } = useThemeContext();
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [avatars, setAvatars] = useState<string[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSave, setOpenSave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [navbarKey, setNavbarKey] = useState(0);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatars`, {
          headers: { Authorization: `Bearer ${Cookies.get('token')}` },
        });
        const data = await response.json();
        setAvatars([DEFAULT_AVATAR, ...(data.avatars || [])]);
      } catch (error) {
        setAvatars([DEFAULT_AVATAR]);
      }
    };
    fetchAvatars();
  }, []);

  useEffect(() => {
    const fetchCurrentAvatar = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/getavatar`, {
          headers: { Authorization: `Bearer ${Cookies.get('token')}` },
        });
        const data = await response.json();
        setSelectedAvatar(data.avatarUrl || DEFAULT_AVATAR);
      } catch (error) {
        setSelectedAvatar(DEFAULT_AVATAR);
      }
    };
    fetchCurrentAvatar();
  }, []);

  useEffect(() => {
    const storedUsername = localStorage.getItem('username') || '';
    const storedEmail = localStorage.getItem('email') || '';
    setFormData(prev => ({
      ...prev,
      username: storedUsername ? decryptData(storedUsername) : '',
      email: storedEmail ? decryptData(storedEmail) : '',
    }));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message });
    setSnackbarOpen(true);
  };

  const handleSaveAvatar = async () => {
    if (!selectedAvatar) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/updateavatar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${Cookies.get('token')}`,
        },
        body: JSON.stringify({ avatar: selectedAvatar }),
      });
      if (response.ok) setNavbarKey(prev => prev + 1);
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  const handleSaveConfirm = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      showNotification('error', 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const token = Cookies.get('token');
      const filteredData: any = {};
      if(formData.username) filteredData.username = formData.username;
      if(formData.email) filteredData.email = formData.email;
      if(formData.newPassword) filteredData.password = formData.newPassword;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(filteredData)
      });

      if (response.ok) {
        // Lekérjük a jelenleg tárolt adatokat a de-titkosítás után
        const currentStoredUsername = decryptData(localStorage.getItem('username') || '');
        const currentStoredEmail = decryptData(localStorage.getItem('email') || '');
        
        // Ellenőrizzük, hogy megváltozott-e a felhasználónév VAGY az email
        const isUsernameChanged = formData.username !== currentStoredUsername;
        const isEmailChanged = formData.email !== currentStoredEmail;

        if (isUsernameChanged || isEmailChanged) {
          // Meghatározzuk az üzenet szövegét attól függően, mi változott
          const changedField = isUsernameChanged && isEmailChanged 
            ? 'Username and email' 
            : isUsernameChanged ? 'Username' : 'Email';

          showNotification('info', `${changedField} changed. You will be logged out.`);
          
          // 3 másodperc várakozás a kijelentkeztetés előtt
          setTimeout(async () => {
            try {
              await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
            } catch (err) {
              console.error("Logout error:", err);
            }
            
            // Sütik és helyi adatok törlése
            Cookies.remove('role');
            Cookies.remove('roles');
            Cookies.remove('token');
            localStorage.clear();
            navigate('/');
          }, 3000);
        } else {
          // Ha csak a jelszó változott (vagy semmi), nem léptetjük ki
          showNotification('success', 'Account updated successfully!');
        }
      }
    } catch (error) {
      showNotification('error', 'An error occurred');
    } finally {
      setLoading(false);
      setOpenSave(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/auth/delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${Cookies.get('token')}` }
      });
      if (response.ok) {
        Cookies.remove('token');
        localStorage.clear();
        navigate('/');
      }
    } finally {
      setLoading(false);
      setOpenDelete(false);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <style>{keyframes}</style>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Particles />
      </div>
      
      <DashBoardNavBar key={navbarKey} />

      <Container sx={{ position: 'relative', zIndex: 1, pt: '100px', pb: 5 }}>
        <Box sx={{ maxWidth: 600, margin: '0 auto', p: 4, borderRadius: '24px', background: darkMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(128,128,128,0.2)', textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.text.primary }}>Account Settings</Typography>
          
          <Box sx={{ mb: 4 }}>
            <Avatar
              src={selectedAvatar || DEFAULT_AVATAR}
              sx={{ width: 120, height: 120, margin: '0 auto 16px', border: `4px solid ${darkMode ? '#ff4081' : '#25379c'}`, cursor: 'pointer', transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } }}
              onClick={() => setOpenDialog(true)}
            />
            <DialogActionButton darkMode={darkMode} onClick={() => setOpenDialog(true)}>
              Change Avatar
            </DialogActionButton>
          </Box>

          <Stack spacing={2}>
            <StyledTextField darkMode={darkMode} fullWidth label="Username" name="username" value={formData.username} onChange={handleChange} />
            <StyledTextField darkMode={darkMode} fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} />
            <StyledTextField darkMode={darkMode} fullWidth label="New Password" name="newPassword" type="password" value={formData.newPassword} onChange={handleChange} />
            <StyledTextField darkMode={darkMode} fullWidth label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} />
            
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
              <DialogActionButton darkMode={darkMode} onClick={() => setOpenSave(true)}>
                Save Changes
              </DialogActionButton>
              <Button onClick={() => setOpenDelete(true)} variant="outlined" color="error" sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 'bold' }}>
                Delete Account
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>

      {/* Select Avatar Dialog */}
      <AnimatedDialog open={openDialog} onClose={() => setOpenDialog(false)} darkMode={darkMode} maxWidth="sm" fullWidth TransitionComponent={Fade} disableScrollLock>
        <DialogTitle>Choose Your Avatar</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 1 }}>
            {avatars.map((avatar) => (
              <Grid item key={avatar}>
                <Avatar
                  src={avatar}
                  sx={{ width: 70, height: 70, cursor: 'pointer', border: selectedAvatar === avatar ? `3px solid ${darkMode ? '#ff4081' : '#25379c'}` : '2px solid transparent', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                  onClick={() => setSelectedAvatar(avatar)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: darkMode ? 'white' : '#1a237e' }}>Cancel</Button>
          <DialogActionButton darkMode={darkMode} onClick={handleSaveAvatar}>Save Avatar</DialogActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* Save Confirmation Dialog */}
      <AnimatedDialog open={openSave} onClose={() => setOpenSave(false)} darkMode={darkMode} TransitionComponent={Fade}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center' }}>
            Are you sure you want to save these account changes?
            {formData.username !== decryptData(localStorage.getItem('username') || '') && (
              <Typography component="span" sx={{ display: 'block', mt: 1, color: '#ff1744', fontWeight: 'bold' }}>
                Note: Changing your username will log you out!
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button onClick={() => setOpenSave(false)} sx={{ color: darkMode ? 'white' : '#1a237e' }}>Cancel</Button>
          <DialogActionButton darkMode={darkMode} onClick={handleSaveConfirm}>Yes, Save</DialogActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* Delete Confirmation Dialog */}
      <AnimatedDialog open={openDelete} onClose={() => setOpenDelete(false)} darkMode={darkMode} TransitionComponent={Fade}>
        <DialogTitle sx={{ color: '#ff1744' }}>Warning: Delete Account</DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center' }}>This action is permanent. Are you sure you want to delete your profile?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button onClick={() => setOpenDelete(false)} sx={{ color: darkMode ? 'white' : '#1a237e' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ borderRadius: '20px', fontWeight: 'bold' }}>Delete Permanently</Button>
        </DialogActions>
      </AnimatedDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={notification?.message}
        action={<IconButton size="small" color="inherit" onClick={() => setSnackbarOpen(false)}><CloseIcon /></IconButton>}
        ContentProps={{
            sx: {
              backgroundColor: notification?.type === 'error' ? 'red' : notification?.type === 'info' ? '#2196f3' : 'green',
              color: 'white'
            }
        }}
      />

      <Backdrop sx={{ color: '#fff', zIndex: 2000 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default Account;